/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentChildren } from 'preact';
import { useMemo } from 'preact/hooks';
import { useId } from '../hooks/UNSAFE_useId';

import { CheckIcon } from '../PRIVATE_ThemedIcons/CheckIcon';

import { BaseMenuItem } from './MenuItem';
import { useSelectMenuGroupContext } from './useSelectMenuGroupContext';

type SelectMenuItemProps = {
  value: string;
  label: string;
  isDisabled?: boolean;
  endIcon?: ComponentChildren;
};

export function SelectMenuItem({ value, label, isDisabled, endIcon }: SelectMenuItemProps) {
  const { isMultiple, value: selectedValue, onCommit } = useSelectMenuGroupContext();
  const uniqueID = useId();
  const id = useMemo(() => `oj-menu-item-${uniqueID}`, [uniqueID]);

  const { isSelected, memoizedOnCommit } = useMemo(() => {
    if (!isMultiple) {
      return {
        isSelected: selectedValue === value,
        memoizedOnCommit: () => {
          onCommit?.({ value, previousValue: selectedValue });
        }
      };
    }
    return {
      isSelected: !!selectedValue?.includes(value),
      memoizedOnCommit: () => {
        const proposedValue = isSelected
          ? selectedValue?.filter((val) => val !== value) || []
          : [...(selectedValue || []), value];
        onCommit?.({ value: proposedValue, previousValue: selectedValue });
      }
    };
  }, [selectedValue, value, onCommit, isMultiple]);

  return (
    <BaseMenuItem
      id={id}
      type="select"
      role={isMultiple ? 'menuitemcheckbox' : 'menuitemradio'}
      isChecked={isSelected}
      isDisabled={isDisabled}
      startIcon={isSelected ? <CheckIcon /> : <span></span>}
      label={label}
      onAction={memoizedOnCommit}
      endIcon={endIcon}
    />
  );
}
